import { useEffect, useState } from "react";

import type { CrmEnrichmentJobType } from "app/pages/sales-intelligence/modules/crm-enrichment/types/types";
import { LOGIN_HEIGHT, LOGIN_WIDTH } from "app/pages/sales-intelligence/pages/management/constants/constants";
import type { Timer } from "app/pages/sales-intelligence/types";

import useHsEnrichmentSettingsQuery from "../queries/useHsEnrichmentSettingsQuery";

const CENTER_DIVISOR = 2;
const QUARTER_DIVISOR = 4;
const WINDOW_TIMEOUT = 1500;

const BASE_PERMISSION_LINK = "/sales-api/sw-connector/hubspot";

const PERMISSION_LINKS = {
    instant: `${BASE_PERMISSION_LINK}/instant-enrichment-connect`,
    scheduled: `${BASE_PERMISSION_LINK}/scheduled-enrichment-connect`,
};

type UsePermissionFlowProps = {
    selectedJobType: CrmEnrichmentJobType | "";
    goNextStep: () => void;
};

const usePermissionFlow = ({ selectedJobType, goNextStep }: UsePermissionFlowProps) => {
    const settingsQuery = useHsEnrichmentSettingsQuery();
    const [connectedWindow, setConnectedWindow] = useState<Window | null>(null);
    const [hasRefetchedJobType, setHasRefetchedJobType] = useState<CrmEnrichmentJobType | "">("");

    const isActivatedStatus =
        selectedJobType === "instant"
            ? settingsQuery.data?.instantEnrichActivated
            : selectedJobType === "scheduled"
              ? settingsQuery.data?.scheduledEnrichActivated
              : false;

    const openPermissionFlow = () => {
        if (!selectedJobType) {
            return;
        }

        const left = (screen.width - LOGIN_WIDTH) / CENTER_DIVISOR;
        const top = (screen.height - LOGIN_HEIGHT) / QUARTER_DIVISOR;
        const windowOptions = `location=0,status=0,titlebar=0,toolbar=0,width=${LOGIN_WIDTH}px,height=${LOGIN_HEIGHT}px,resizable=yes,left=${left}%,top=${top}`;

        const connected = window.open(PERMISSION_LINKS[selectedJobType], "_blank", windowOptions);
        setConnectedWindow(connected);
    };

    useEffect(() => {
        if (!connectedWindow) {
            return;
        }

        const timer: Timer = setInterval(() => {
            if (connectedWindow?.closed) {
                setConnectedWindow(null);
                setHasRefetchedJobType(selectedJobType);
                settingsQuery.refetch();
                clearInterval(timer);
            }
        }, WINDOW_TIMEOUT);

        return () => clearInterval(timer);
    }, [connectedWindow]);

    useEffect(() => {
        setHasRefetchedJobType("");
    }, [selectedJobType]);

    useEffect(() => {
        if (isActivatedStatus && hasRefetchedJobType === selectedJobType) {
            goNextStep();
        }
    }, [isActivatedStatus, hasRefetchedJobType, selectedJobType]);

    return { openPermissionFlow, isActivatedStatus };
};

export default usePermissionFlow;
